/**
 * MPC (Mothership Prompt Compiler) Conversation Storage
 *
 * Client-side storage for conversation history, persistence of input/mode state.
 * Uses localStorage with versioned schema for future migrations.
 */

// Logging prefix for easy filtering
const LOG_PREFIX = '[MPC-Storage]';

const log = (action: string, ...args: any[]) => {
  console.log(`${LOG_PREFIX} ${action}`, ...args);
};

const logError = (action: string, error: any) => {
  console.error(`${LOG_PREFIX} ${action} ERROR:`, error);
};

// Schema version for future migrations
const SCHEMA_VERSION = 1;

// Storage keys
const STORAGE_KEYS = {
  CONVERSATIONS: 'mpc-conversations',
  LAST_INPUT: 'mpc-last-input',
  LAST_MODE: 'mpc-last-mode',
  SCHEMA_VERSION: 'mpc-schema-version',
} as const;

// Limits
const MAX_CONVERSATIONS = 50;
const MAX_TURNS_PER_CONVERSATION = 100;

// Types
export type TurnRole = 'chatgpt-question' | 'claude-response';
export type ConversationMode = 'normal' | 'back-and-forth';
export type ConversationStatus = 'active' | 'completed';

export interface ConversationTurn {
  id: string;
  timestamp: string;
  role: TurnRole;
  content: string;
}

export interface Conversation {
  id: string;
  createdAt: string;
  updatedAt: string;
  mode: ConversationMode;
  status: ConversationStatus;
  turns: ConversationTurn[];
}

export interface StorageData {
  schemaVersion: number;
  conversations: Conversation[];
}

// Generate unique ID
const generateId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// Get current ISO timestamp
const now = (): string => new Date().toISOString();

// Safe JSON parse with fallback
const safeParse = <T>(json: string | null, fallback: T): T => {
  if (!json) return fallback;
  try {
    return JSON.parse(json) as T;
  } catch {
    return fallback;
  }
};

// Initialize storage with schema version check
const initStorage = (): StorageData => {
  log('initStorage', 'Loading storage data...');
  const storedVersion = localStorage.getItem(STORAGE_KEYS.SCHEMA_VERSION);
  const rawData = localStorage.getItem(STORAGE_KEYS.CONVERSATIONS);
  const data = safeParse<StorageData>(
    rawData,
    { schemaVersion: SCHEMA_VERSION, conversations: [] }
  );

  log('initStorage', `Found ${data.conversations.length} conversations, schema v${storedVersion || 'null'}`);

  // Future: handle migrations here based on storedVersion vs SCHEMA_VERSION
  if (!storedVersion || parseInt(storedVersion) !== SCHEMA_VERSION) {
    log('initStorage', `Updating schema version from ${storedVersion} to ${SCHEMA_VERSION}`);
    localStorage.setItem(STORAGE_KEYS.SCHEMA_VERSION, String(SCHEMA_VERSION));
  }

  return data;
};

// Save storage data
const saveStorage = (data: StorageData): void => {
  const jsonStr = JSON.stringify(data);
  log('saveStorage', `Saving ${data.conversations.length} conversations (${jsonStr.length} bytes)`);
  localStorage.setItem(STORAGE_KEYS.CONVERSATIONS, jsonStr);
};

// Enforce limits (oldest-first cleanup)
const enforceLimits = (data: StorageData): StorageData => {
  const originalCount = data.conversations.length;

  // Limit conversations
  if (data.conversations.length > MAX_CONVERSATIONS) {
    log('enforceLimits', `Trimming conversations from ${data.conversations.length} to ${MAX_CONVERSATIONS}`);
    data.conversations = data.conversations
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
      .slice(0, MAX_CONVERSATIONS);
  }

  // Limit turns per conversation
  let turnsTrimmed = 0;
  data.conversations = data.conversations.map(conv => {
    if (conv.turns.length > MAX_TURNS_PER_CONVERSATION) {
      turnsTrimmed += conv.turns.length - MAX_TURNS_PER_CONVERSATION;
    }
    return {
      ...conv,
      turns: conv.turns.slice(-MAX_TURNS_PER_CONVERSATION)
    };
  });

  if (turnsTrimmed > 0) {
    log('enforceLimits', `Trimmed ${turnsTrimmed} excess turns`);
  }

  return data;
};

// ============ Conversation Operations ============

export const getAllConversations = (): Conversation[] => {
  log('getAllConversations', 'Fetching all conversations');
  const data = initStorage();
  const sorted = data.conversations.sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  );
  log('getAllConversations', `Returning ${sorted.length} conversations`);
  return sorted;
};

export const getConversation = (id: string): Conversation | null => {
  log('getConversation', `Looking up conversation ${id}`);
  const data = initStorage();
  const conv = data.conversations.find(c => c.id === id) || null;
  log('getConversation', conv ? `Found: ${conv.turns.length} turns, status=${conv.status}` : 'Not found');
  return conv;
};

export const createConversation = (mode: ConversationMode): Conversation => {
  log('createConversation', `Creating new ${mode} conversation`);
  const data = initStorage();
  const conversation: Conversation = {
    id: generateId(),
    createdAt: now(),
    updatedAt: now(),
    mode,
    status: 'active',
    turns: [],
  };

  data.conversations.unshift(conversation);
  saveStorage(enforceLimits(data));
  log('createConversation', `Created conversation ${conversation.id}`);
  return conversation;
};

export const addTurn = (
  conversationId: string,
  role: TurnRole,
  content: string
): ConversationTurn | null => {
  log('addTurn', `Adding ${role} turn to ${conversationId} (${content.length} chars)`);
  const data = initStorage();
  const conv = data.conversations.find(c => c.id === conversationId);

  if (!conv) {
    logError('addTurn', `Conversation ${conversationId} not found`);
    return null;
  }

  if (conv.status === 'completed') {
    logError('addTurn', `Conversation ${conversationId} is completed, cannot add turns`);
    return null;
  }

  const turn: ConversationTurn = {
    id: generateId(),
    timestamp: now(),
    role,
    content,
  };

  conv.turns.push(turn);
  conv.updatedAt = now();
  saveStorage(enforceLimits(data));
  log('addTurn', `Added turn ${turn.id}, conversation now has ${conv.turns.length} turns`);
  return turn;
};

export const completeConversation = (conversationId: string): boolean => {
  log('completeConversation', `Completing conversation ${conversationId}`);
  const data = initStorage();
  const conv = data.conversations.find(c => c.id === conversationId);

  if (!conv) {
    logError('completeConversation', `Conversation ${conversationId} not found`);
    return false;
  }

  conv.status = 'completed';
  conv.updatedAt = now();
  saveStorage(data);
  log('completeConversation', `Conversation ${conversationId} marked completed`);
  return true;
};

export const reopenConversation = (conversationId: string): boolean => {
  log('reopenConversation', `Reopening conversation ${conversationId}`);
  const data = initStorage();
  const conv = data.conversations.find(c => c.id === conversationId);

  if (!conv) {
    logError('reopenConversation', `Conversation ${conversationId} not found`);
    return false;
  }

  conv.status = 'active';
  conv.updatedAt = now();
  saveStorage(data);
  log('reopenConversation', `Conversation ${conversationId} reopened`);
  return true;
};

export const deleteConversation = (conversationId: string): boolean => {
  log('deleteConversation', `Deleting conversation ${conversationId}`);
  const data = initStorage();
  const idx = data.conversations.findIndex(c => c.id === conversationId);

  if (idx === -1) {
    logError('deleteConversation', `Conversation ${conversationId} not found`);
    return false;
  }

  data.conversations.splice(idx, 1);
  saveStorage(data);
  log('deleteConversation', `Conversation ${conversationId} deleted`);
  return true;
};

// ============ Persistence Operations ============

export const getLastInput = (): string => {
  const input = localStorage.getItem(STORAGE_KEYS.LAST_INPUT) || '';
  log('getLastInput', `Loaded ${input.length} chars`);
  return input;
};

export const saveLastInput = (input: string): void => {
  log('saveLastInput', `Saving ${input.length} chars`);
  localStorage.setItem(STORAGE_KEYS.LAST_INPUT, input);
};

export const getLastMode = (): ConversationMode => {
  const mode = localStorage.getItem(STORAGE_KEYS.LAST_MODE);
  const result = mode === 'back-and-forth' ? 'back-and-forth' : 'normal';
  log('getLastMode', `Loaded mode: ${result}`);
  return result;
};

export const saveLastMode = (mode: ConversationMode): void => {
  log('saveLastMode', `Saving mode: ${mode}`);
  localStorage.setItem(STORAGE_KEYS.LAST_MODE, mode);
};

// ============ Clear Operations ============

export const clearAllHistory = (): void => {
  log('clearAllHistory', 'Clearing all MPC storage data');
  localStorage.removeItem(STORAGE_KEYS.CONVERSATIONS);
  localStorage.removeItem(STORAGE_KEYS.LAST_INPUT);
  localStorage.removeItem(STORAGE_KEYS.LAST_MODE);
  localStorage.removeItem(STORAGE_KEYS.SCHEMA_VERSION);
  log('clearAllHistory', 'All MPC storage data cleared');
};

// ============ Utility Functions ============

export const getExpectedNextRole = (conversation: Conversation): TurnRole => {
  if (conversation.turns.length === 0) {
    return 'chatgpt-question';
  }
  const lastRole = conversation.turns[conversation.turns.length - 1].role;
  return lastRole === 'chatgpt-question' ? 'claude-response' : 'chatgpt-question';
};

export const formatTimestamp = (timestamp: string): string => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffMins < 1440) {
    const hours = Math.floor(diffMins / 60);
    return `${hours}h ago`;
  }
  return date.toLocaleDateString();
};
